import { useCallback, useEffect, useState } from "react";
import "@/styles/dashboard.css";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { AttentionQueue } from "@/components/dashboard/AttentionQueue";
import { ChartCard, FillRateChart } from "@/components/dashboard/DashboardCharts";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardError, DashboardSkeleton } from "@/components/dashboard/DashboardStates";
import { MetricCards, buildMetricCards } from "@/components/dashboard/MetricCards";
import { NeedsYouPanel } from "@/components/dashboard/NeedsYouPanel";
import { PayrollReadinessCard } from "@/components/dashboard/PayrollReadinessCard";
import { PriorityPanel } from "@/components/dashboard/PriorityPanel";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import ClientDashboard from "@/pages/ClientDashboard";

const CREATE_ROLES = ["operations", "staffing_coordinator", "system_administrator"];

const metricValue = (dashboard, label) => Number(dashboard.metrics.find((item) => item.label === label)?.value || 0);

const chartSubtitle = (dashboard) => {
  const fill = dashboard.fill_rate || {};
  const latest = [...(dashboard.monthly_fill_rate || [])].pop();
  const toDate = latest ? `${latest.month} to date` : "";
  const filled = fill.total ? `${fill.filled} of ${fill.total} positions filled` : "No positions this school year";
  return [`School year ${dashboard.school_year?.label || ""}`, filled, toDate].filter(Boolean).join(" · ");
};

export default function DashboardPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/dashboard/summary", { params: clientId ? { client_id: clientId } : {} });
      setDashboard(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (isClient) {
    if (error)
      return (
        <div className="app-loading error-loading" data-testid="dashboard-error">
          {error}
        </div>
      );
    if (!dashboard)
      return (
        <div className="app-loading" data-testid="dashboard-loading">
          Loading operational intelligence…
        </div>
      );
    return <ClientDashboard dashboard={dashboard} user={user} />;
  }

  const target = dashboard?.fill_rate?.target ?? 85;

  return (
    <div className="app-shell" data-testid="dashboard-shell">
      <AppSidebar />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page ops-dashboard" data-testid="executive-dashboard" aria-busy={loading}>
          <DashboardHeader
            user={user}
            schoolYear={dashboard?.school_year}
            canCreate={CREATE_ROLES.includes(user?.role)}
            clientOptions={dashboard?.client_options || []}
            clientId={clientId}
            onClientChange={setClientId}
            filterDisabled={!dashboard && loading}
          />
          {!clientId && <NeedsYouPanel user={user} />}
          {!dashboard && error && <DashboardError message={error} onRetry={load} />}
          {!dashboard && !error && <DashboardSkeleton />}
          {dashboard && (
            <div className={`ops-body ${loading ? "is-refreshing" : ""}`} data-testid="dashboard-body">
              {error && <DashboardError inline message={error} onRetry={load} />}
              <MetricCards cards={buildMetricCards(dashboard)} />
              {["executive", "system_administrator", "hr", "operations", "staffing_coordinator"].includes(user?.role) && !clientId && <PayrollReadinessCard user={user} />}
              <section className="ops-main-row">
                <ChartCard title="Staffing performance" subtitle={chartSubtitle(dashboard)} testId="fill-rate-chart">
                  <FillRateChart series={dashboard.monthly_fill_rate} target={target} />
                </ChartCard>
                <PriorityPanel
                  attention={dashboard.needs_attention}
                  statusBreakdown={dashboard.status_breakdown}
                  openCount={metricValue(dashboard, "Open Positions")}
                />
              </section>
              <AttentionQueue attention={dashboard.needs_attention} coverage={dashboard.client_coverage} clientScoped={Boolean(clientId)} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
